import { createConsumer, Consumer, Subscription } from '../lib/websocket';
import { env } from '../config/env';
import { logger } from './logger';

const WS_URL = env.wsUrl;

export interface WidgetChannelEvents {
  call_end: { call_id: number; end_by: string; customer_id: number; user_id: number };
  call_accept: { call_id: number; customer_id: number; user_id: number; user_name: string };
  basket_products_updated: { customer_id: number };
  wish_products_updated: { customer_id: number };
  user_is_offline: { call_id: number; customer_id: number; user_name: string };
  user_is_busy: { call_id: number; customer_id: number; user_name: string };
  call_dont_pickup: { call_id: number; customer_id: number };
  seller_assigned: { call_id: number; seller: { id: number; full_name: string; image?: string } };
  // W5: the OTHER participant moved between video / audio / chat. Portal never
  // echoes a side's own change back to it, so on this channel `participant_type`
  // is always 'seller' — it is carried anyway so the payload reads the same on
  // both sides of the meeting, and so a stray one can be ignored rather than
  // mistaken for the seller.
  participant_mode_changed: { call_id: number; participant_type: 'customer' | 'seller'; mode: 'video' | 'audio' | 'chat' };
}

type EventHandler<T> = (data: T) => void;

type ConnectionChangeHandler = (connected: boolean) => void;

class PortalWebSocket {
  private consumer: Consumer | null = null;
  private subscription: Subscription | null = null;
  private handlers: Map<string, Set<EventHandler<any>>> = new Map();
  private connectionChangeHandlers: Set<ConnectionChangeHandler> = new Set();

  connect(customerId: number, wssSign: string): void {
    const url = `${WS_URL}?wss_sign=${wssSign}&id=${customerId}&location=w`;
    this.consumer = createConsumer(url);

    this.subscription = this.consumer.subscriptions.create(
      { channel: 'WidgetChannel' },
      {
        received: (data: { event: string; data: any }) => {
          this.handleEvent(data.event, data.data);
        },
        connected: () => {
          logger.info('WidgetChannel connected');
          this.notifyConnectionChange(true);
        },
        disconnected: ({ willAttemptReconnect }: { willAttemptReconnect: boolean }) => {
          logger.info('WidgetChannel disconnected', { willAttemptReconnect });
          this.notifyConnectionChange(false);
        },
      }
    );
  }

  disconnect(): void {
    this.subscription?.unsubscribe();
    this.consumer?.disconnect();
    this.consumer = null;
    this.subscription = null;
  }

  on<K extends keyof WidgetChannelEvents>(
    event: K,
    handler: EventHandler<WidgetChannelEvents[K]>
  ): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    return () => this.handlers.get(event)?.delete(handler);
  }

  /**
   * Subscribe to WebSocket connection state changes.
   * Handler receives `true` on connect, `false` on disconnect.
   * Returns unsubscribe function.
   */
  onConnectionChange(handler: ConnectionChangeHandler): () => void {
    this.connectionChangeHandlers.add(handler);
    return () => this.connectionChangeHandlers.delete(handler);
  }

  private handleEvent(event: string, data: any): void {
    const handlers = this.handlers.get(event);
    handlers?.forEach(handler => handler(data));
  }

  private notifyConnectionChange(connected: boolean): void {
    this.connectionChangeHandlers.forEach(handler => handler(connected));
  }
}

export const portalWs = new PortalWebSocket();
